import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, CalendarDays } from "lucide-react";
import Calendar from "../../calendar";
import "../../calendar/Calendar.css";
import {
  createScheduledMeeting,
  updateScheduledMeeting,
  getScheduledMeetingById,
} from "../../../services/roomServices";
import { toast } from "react-toastify";
import { TIMEZONES } from "../../../utils/timezones";

const ToggleSwitch = ({ enabled, onToggle }) => (
  <button
    onClick={onToggle}
    className={`relative inline-flex w-[33px] h-[19px] items-center rounded-full transition-colors bg-[#282828]`}
  >
    <span
      className={`inline-block w-4 h-4 transform rounded-full transition-transform ${
        enabled
          ? "bg-[#832CFF] translate-x-[15px]"
          : "bg-[#7F7F7F] translate-x-0.5"
      }`}
    />
  </button>
);

const UploadFile = ({
  onFileSelect,
  file,
  label = "Custom Background",
  recommendedText,
}) => {
  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileSelect = (selectedFile) => {
    if (selectedFile) {
      // Validate file size (3 MB max)
      if (selectedFile.size > 3 * 1024 * 1024) {
        toast.error("File size must be less than 3 MB");
        return;
      }

      // Validate file type
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/bmp",
        "image/webp",
      ];
      if (!validTypes.includes(selectedFile.type)) {
        toast.error("Please select a valid image file (JPG, PNG, etc.)");
        return;
      }

      if (onFileSelect) {
        onFileSelect(selectedFile);
      }
    }
  };

  return (
    <div>
      <div className="flex flex-col items-center gap-2 mt-4">
        <div
          className={`relative max-w-[400px] rounded-lg border-gray-400 p-3 transition-colors bg-[#0000001F] hover:border-gray-300 cursor-pointer`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <div
            className="border-2 border-dashed p-5 rounded-lg"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <label className="flex flex-col items-center justify-center cursor-pointer">
              <div className="text-center">
                <p className="text-gray-400 font-montserrat text-md font-bold">
                  Click Here or Drag and Drop
                </p>
                {file && <p className="text-white text-sm mt-2">{file.name}</p>}
              </div>
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.bmp,.webp"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleFileSelect(e.target.files[0]);
                  }
                }}
              />
            </label>
          </div>
        </div>
      </div>
      <p className="text-[#7F7F7F] mb-6">
        {recommendedText ||
          "Recommended: 1920 x 1080 for best quality. Max file size: 3 MB. JPG or PNG. Leave blank to display R-Link's default image."}
      </p>
      {file && (
        <div className="flex justify-center mb-4">
          <img
            src={URL.createObjectURL(file)}
            alt="Preview"
            className="max-w-[400px] max-h-[200px] rounded-md object-contain"
          />
        </div>
      )}
      <div className="flex justify-center">
        <div className="bg-[#9492AD] text-white rounded-md px-4 py-3">
          Preview Screen
        </div>
      </div>
    </div>
  );
};

const DurationButton = ({ value, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
      selected
        ? "bg-[#832CFF] text-white"
        : "bg-[#0000001F] text-white hover:bg-opacity-20"
    }`}
  >
    {value}
  </button>
);

const OptionButton = ({ label, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 sm:px-5 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
      selected
        ? "bg-[#832CFF] text-white"
        : "bg-[#0000001F] text-white hover:bg-opacity-20"
    }`}
  >
    {label}
  </button>
);

const TIME_OPTIONS = (() => {
  const minutes = ["00", "15", "30", "45"];
  const options = [];
  for (let hour = 1; hour <= 12; hour += 1) {
    minutes.forEach((minute) => {
      options.push({
        value: `${hour}-${minute}`,
        label: `${hour}:${minute}`,
      });
    });
  }
  return options;
})();

const WebinarScheduleForm = ({ initialData = null, roomId = null }) => {
  const navigate = useNavigate();
  const timezones = TIMEZONES;

  const isEditMode = !!roomId || !!initialData;

  // Get current date and set it as default
  const getCurrentDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Get current time in 12-hour format
  const getCurrentTime = () => {
    const now = new Date();
    let hours = now.getHours();
    let minutes = now.getMinutes();

    // Round minutes to nearest 15-minute interval
    const roundedMinutes = Math.round(minutes / 15) * 15;

    // If rounded to 60, set to 0 and increment hour
    if (roundedMinutes === 60) {
      minutes = 0;
      hours = (hours + 1) % 24;
    } else {
      minutes = roundedMinutes;
    }

    const period = hours >= 12 ? "PM" : "AM";

    if (hours === 0) {
      hours = 12;
    } else if (hours > 12) {
      hours = hours - 12;
    }

    return {
      hour: hours,
      minute: minutes.toString().padStart(2, "0"),
      period,
    };
  };

  // Parse date from API response
  const parseDate = (dateString) => {
    if (!dateString) return getCurrentDate();
    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  // Parse time from API response (ISO string)
  const parseTimeFromISO = (isoString) => {
    if (!isoString) return getCurrentTime();
    const date = new Date(isoString);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? "PM" : "AM";

    if (hours === 0) {
      hours = 12;
    } else if (hours > 12) {
      hours = hours - 12;
    }

    return {
      hour: hours,
      minute: minutes.toString().padStart(2, "0"),
      period,
    };
  };

  // Parse password string into array
  const parsePassword = (passwordString) => {
    if (!passwordString) return ["", "", "", "", "", ""];
    const passwordArray = passwordString.split("").slice(0, 6);
    while (passwordArray.length < 6) {
      passwordArray.push("");
    }
    return passwordArray;
  };

  const currentTime = getCurrentTime();
  const [formData, setFormData] = useState({
    webinarTitle: "",
    description: "",
    recurringEvent: true,
    frequency: "WEEKLY",
    startDate: getCurrentDate(),
    endDate: getCurrentDate(),
    startHour: currentTime.hour,
    startMinute: currentTime.minute,
    startPeriod: currentTime.period,
    duration: "60",
    customDuration: "",
    timezone: "America/New_York (GMT-05:00)",

    allowStageAccess: true,
    allowCoHostsManage: true,
    autoSpotlight: true,
    preAssignPresenters: false,
    presenterEmails: [],
    presenterEmailInput: "",

    showAttendeeList: true,
    showAttendeeCount: true,
    allowChat: true,
    allowReactions: true,

    customBackground: false,
    requireRegistration: false,
    registrationTitle: "",
    registrationSubtitle: "",

    countdownTimer: false,
    countdownMinutes: "15",
    countdownCustomMinutes: "",
    waitingRoomTitle: "",
    waitingRoomMessage: "",

    autoRecordWebinar: true,
    autoShareReplay: true,
    replayAudience: "attendees",

    requirePassword: false,
    password: ["", "", "", "", "", ""],
    showPasswordInEmail: false,
    // Streaming (section 6)
    streamYouTubeUrl: "",
    streamYouTubeKey: "",
    streamFacebookUrl: "",
    streamFacebookKey: "",
    streamLinkedInUrl: "",
    streamLinkedInKey: "",
  });

  const [files, setFiles] = useState({
    customBackgroundImage: null,
    registrationCoverImage: null,
    waitingRoomCoverImage: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFormHidden, setIsFormHidden] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [streamingToggle, setStreamingToggle] = useState({
    streamYouTube: false,
    streamFacebook: false,
    streamLinkedIn: false,
  });
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Map API data to form data structure
  const mapApiDataToFormData = (apiData) => {
    if (!apiData) return null;

    const startTime = parseTimeFromISO(apiData.scheduledStartTime);
    const startDate = parseDate(apiData.scheduledStartTime);
    const endDate = apiData.scheduledEndTime
      ? parseDate(apiData.scheduledEndTime)
      : parseDate(apiData.scheduledStartTime);

    return {
      webinarTitle: apiData.roomName ? apiData.roomName.slice(0, 100) : "",
      description: apiData.description || "",
      recurringEvent: apiData.recurringEvent ?? true,
      frequency: apiData.frequency || "",
      startDate: startDate,
      endDate: endDate,
      startHour: startTime.hour,
      startMinute: startTime.minute,
      startPeriod: startTime.period,
      duration: apiData.duration || "60",
      customDuration: apiData.customDuration || "",
      timezone: apiData.timezone
        ? `${apiData.timezone} (${apiData.timezone})`
        : "America/New_York (GMT-05:00)",
      allowStageAccess: apiData.allowStageAccess ?? true,
      allowCoHostsManage: apiData.allowCoHostsManage ?? true,
      autoSpotlight: apiData.autoSpotlight ?? true,
      preAssignPresenters: apiData.preAssignPresenters ?? false,
      presenterEmails: apiData.emails || [],
      presenterEmailInput: "",
      showAttendeeList: apiData.showAttendeeList ?? true,
      showAttendeeCount: apiData.showAttendeeCount ?? true,
      allowChat: apiData.allowChat ?? true,
      allowReactions: apiData.allowReactions ?? true,
      customBackground: apiData.customBackgroundEnabled ?? true,
      requireRegistration: apiData.requireRegistration ?? true,
      registrationTitle: apiData.registrationTitle || "",
      registrationSubtitle: apiData.registrationSubtitle || "",
      countdownTimer: apiData.countdownTimer ?? true,
      countdownMinutes: apiData.countdownMinutes
        ? apiData.countdownMinutes.toString()
        : "15",
      countdownCustomMinutes: apiData.countdownCustomMinutes || "",
      waitingRoomTitle: apiData.waitingRoomTitle || "",
      waitingRoomMessage: apiData.waitingRoomMessage || "",
      autoRecordWebinar: apiData.autoRecordWebinar ?? true,
      autoShareReplay: apiData.autoShareReplay ?? true,
      replayAudience: apiData.replayAudience || "attendees",
      requirePassword: apiData.requirePassword ?? false,
      password: parsePassword(apiData.password),
      showPasswordInEmail: apiData.showPasswordInEmail ?? false,
      streamYouTubeUrl: apiData.streamYouTubeUrl || "",
      streamYouTubeKey: apiData.streamYouTubeKey || "",
      streamFacebookUrl: apiData.streamFacebookUrl || "",
      streamFacebookKey: apiData.streamFacebookKey || "",
      streamLinkedInUrl: apiData.streamLinkedInUrl || "",
      streamLinkedInKey: apiData.streamLinkedInKey || "",
    };
  };

  // Fetch data if roomId is provided
  useEffect(() => {
    const fetchMeetingData = async () => {
      if (roomId && !initialData) {
        setIsLoading(true);
        try {
          const response = await getScheduledMeetingById(roomId);
          if (response?.data) {
            const mappedData = mapApiDataToFormData(response.data);
            if (mappedData) {
              setFormData(mappedData);
            }
          }
        } catch (error) {
          console.error("Error fetching meeting data:", error);
          toast.error("Failed to load meeting data");
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchMeetingData();
  }, [roomId]);

  // Preload data from initialData prop
  useEffect(() => {
    if (initialData) {
      const mappedData = mapApiDataToFormData(initialData);
      if (mappedData) {
        setFormData(mappedData);
      }
    }
  }, [initialData]);

  useEffect(() => {
    const platforms = [
      {
        key: "streamYouTube",
        url: formData.streamYouTubeUrl,
        keyField: formData.streamYouTubeKey,
      },
      {
        key: "streamFacebook",
        url: formData.streamFacebookUrl,
        keyField: formData.streamFacebookKey,
      },
      {
        key: "streamLinkedIn",
        url: formData.streamLinkedInUrl,
        keyField: formData.streamLinkedInKey,
      },
    ];

    setStreamingToggle((prev) => {
      const updated = { ...prev };
      let changed = false;

      platforms.forEach(({ key, url, keyField }) => {
        const hasData = Boolean(url || keyField);
        if (prev[key] !== hasData) {
          updated[key] = hasData;
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [
    formData.streamYouTubeUrl,
    formData.streamYouTubeKey,
    formData.streamFacebookUrl,
    formData.streamFacebookKey,
    formData.streamLinkedInUrl,
    formData.streamLinkedInKey,
  ]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const setFieldError = (field, message) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const clearFieldError = (field) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handlePasswordChange = (index, value) => {
    const newPassword = [...formData.password];
    newPassword[index] = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setFormData((prev) => ({ ...prev, password: newPassword }));
  };

  const handlePresenterEmailAdd = () => {
    const email = formData.presenterEmailInput.trim();
    if (!email) {
      toast.error("Please enter an email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (formData.presenterEmails.includes(email)) {
      toast.error("Email already added");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      presenterEmails: [...prev.presenterEmails, email],
      presenterEmailInput: "",
    }));
  };

  const handlePresenterEmailRemove = (emailToRemove) => {
    setFormData((prev) => ({
      ...prev,
      presenterEmails: prev.presenterEmails.filter(
        (email) => email !== emailToRemove
      ),
    }));
  };

  const handleFileSelect = (fileType, file) => {
    setFiles((prev) => ({ ...prev, [fileType]: file }));
  };

  const formatDisplayDate = (dateValue) => {
    if (!dateValue) return "";
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const parsedDuration = parseInt(formData.duration, 10);
  const normalizedDuration = Number.isNaN(parsedDuration) ? 0 : parsedDuration;
  const durationHours = Math.floor(normalizedDuration / 60);
  const durationMinutes = normalizedDuration % 60;

  const calculateRecurrenceSummary = () => {
    if (!formData.startDate) {
      return "Please select a start date";
    }

    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    const startTime = `${formData.startHour}:${formData.startMinute} ${formData.startPeriod}`;

    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const dayName = dayNames[startDate.getDay()];

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const endDateStr = `${
      monthNames[endDate.getMonth()]
    } ${endDate.getDate()} ${endDate.getFullYear()}`;

    const frequencyMap = {
      DAILY: "day",
      WEEKLY: dayName,
      MONTHLY: "month",
    };

    const frequencyText =
      frequencyMap[formData.frequency] || formData.frequency.toLowerCase();

    return `Occurs every ${frequencyText} at ${startTime} until ${endDateStr}`;
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate webinar title
    if (!formData.webinarTitle.trim()) {
      newErrors.webinarTitle = "Please enter a webinar title";
    } else if (formData.webinarTitle.trim().length > 100) {
      newErrors.webinarTitle = "Webinar title must be 100 characters or less";
    }

    // Validate dates
    if (!formData.startDate) {
      newErrors.startDate = "Please select a start date";
    }

    // if (!formData.endDate) {
    //   newErrors.endDate = "Please select an end date";
    // }

    // Validate date range
    // if (formData.startDate && formData.endDate) {
    //   const startDate = new Date(formData.startDate);
    //   const endDate = new Date(formData.endDate);

    //   // Reset hours to compare dates only
    //   startDate.setHours(0, 0, 0, 0);
    //   endDate.setHours(0, 0, 0, 0);

    //   if (endDate < startDate) {
    //     newErrors.endDate = "End date must be after or equal to start date";
    //   }
    // }

    // Validate duration
    if (formData.customDuration) {
      const customDur = parseInt(formData.customDuration);
      if (isNaN(customDur) || customDur < 1) {
        newErrors.customDuration =
          "Custom duration must be a valid number greater than 0";
      }
    }

    // Validate password if required
    if (formData.requirePassword) {
      const passwordStr = formData.password.join("");
      if (passwordStr.length < 4 || passwordStr.length > 6) {
        newErrors.password = "Password must be 4-6 characters";
      }
    }

    // Validate registration fields if required
    // if (formData.requireRegistration) {
    //   if (formData.registrationTitle.trim().length > 25) {
    //     newErrors.registrationTitle =
    //       "Registration title must be 25 characters or less";
    //   }
    //   if (formData.registrationSubtitle.trim().length > 25) {
    //     newErrors.registrationSubtitle =
    //       "Registration subtitle must be 25 characters or less";
    //   }
    // }

    // Validate waiting room fields
    // if (formData.waitingRoomTitle.trim().length > 25) {
    //   newErrors.waitingRoomTitle =
    //     "Waiting room title must be 25 characters or less";
    // }
    // if (formData.waitingRoomMessage.trim().length > 25) {
    //   newErrors.waitingRoomMessage =
    //     "Waiting room message must be 25 characters or less";
    // }

    // Validate countdown custom minutes
    if (formData.countdownTimer && formData.countdownCustomMinutes) {
      const countdownMins = parseInt(formData.countdownCustomMinutes);
      if (isNaN(countdownMins) || countdownMins < 1) {
        newErrors.countdownCustomMinutes =
          "Countdown minutes must be a valid number greater than 0";
      }
    }

    console.log(newErrors, "newErrors");
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors in the form");
      return false;
    }

    return true;
  };

  const formatDateTime = () => {
    if (!formData.startDate) return null;

    const date = new Date(formData.startDate);
    let hours = parseInt(formData.startHour, 10);
    const minutes = parseInt(formData.startMinute, 10);

    if (formData.startPeriod === "PM" && hours !== 12) {
      hours += 12;
    } else if (formData.startPeriod === "AM" && hours === 12) {
      hours = 0;
    }

    date.setHours(hours, minutes, 0, 0);

    // Format manually to remove milliseconds and Z
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hour = String(date.getHours()).padStart(2, "0");
    const minute = String(date.getMinutes()).padStart(2, "0");
    const second = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();

      // Basic webinar data
      formDataToSend.append("roomName", formData.webinarTitle.trim());
      formDataToSend.append("roomType", "Webinar");

      // Handle timezone - extract timezone value from TIMEZONES array format
      // Extract the timezone identifier (e.g., "America/New_York" from "America/New_York (GMT-05:00)")
      let timezoneValue = "America/New_York";
      if (formData.timezone) {
        if (typeof formData.timezone === "string") {
          // If it's already a string with parentheses, extract the timezone part
          if (formData.timezone.includes("(")) {
            timezoneValue = formData.timezone.split("(")[0].trim();
          } else {
            // If it's a timezone object, get the value
            const selectedTimezone = timezones.find(
              (tz) =>
                tz.value === formData.timezone || tz.label === formData.timezone
            );
            if (selectedTimezone) {
              timezoneValue = selectedTimezone.value.split("(")[0].trim();
            } else {
              timezoneValue = formData.timezone.split(" ")[0];
            }
          }
        }
      }
      formDataToSend.append("timezone", timezoneValue);

      // Date and time
      const startDateTime = formatDateTime();
      console.log(startDateTime, "startDateTime");
      if (startDateTime) {
        formDataToSend.append("startTime", startDateTime);
      }

      // Add individual time fields
      formDataToSend.append("startTimeHour", formData.startHour.toString());
      formDataToSend.append("startTimeMinute", formData.startMinute);
      formDataToSend.append("StartTimeAmPm", formData.startPeriod);

      // Calculate end time based on duration
      const duration = formData.customDuration
        ? parseInt(formData.customDuration)
        : parseInt(formData.duration);

      // Add duration to request body
      formDataToSend.append("duration", duration.toString());
      formDataToSend.append("WebinarDuration", duration.toString());

      if (startDateTime) {
        const endDateTime = new Date(
          new Date(startDateTime).getTime() + duration * 60 * 1000
        );
        formDataToSend.append("endTime", endDateTime.toISOString());
      }

      // Recurring event settings
      formDataToSend.append("recurringEvent", formData.recurringEvent);
      if (formData.recurringEvent) {
        formDataToSend.append("frequency", formData.frequency);
        formDataToSend.append(
          "endDate",
          new Date(formData.endDate).toISOString()
        );
      }

      // Description
      if (formData.description) {
        formDataToSend.append("description", formData.description);
      }

      // Stage Control settings
      formDataToSend.append("allowStageAccess", formData.allowStageAccess);
      formDataToSend.append("allowCoHostsManage", formData.allowCoHostsManage);
      formDataToSend.append("autoSpotlight", formData.autoSpotlight);
      formDataToSend.append(
        "preAssignPresenters",
        formData.preAssignPresenters
      );

      if (formData.preAssignPresenters && formData.presenterEmails.length > 0) {
        formDataToSend.append(
          "emails",
          JSON.stringify(formData.presenterEmails)
        );
      }

      // Attendee Options
      formDataToSend.append("showAttendeeList", formData.showAttendeeList);
      formDataToSend.append("showAttendeeCount", formData.showAttendeeCount);
      formDataToSend.append("allowChat", formData.allowChat);
      formDataToSend.append("allowReactions", formData.allowReactions);

      // Custom Background
      formDataToSend.append(
        "customBackgroundEnabled",
        formData.customBackground
      );
      if (formData.customBackground && files.customBackgroundImage) {
        formDataToSend.append("backgroundImage", files.customBackgroundImage);
      }

      // Registration settings
      formDataToSend.append(
        "requireRegistration",
        formData.requireRegistration
      );
      if (formData.requireRegistration) {
        formDataToSend.append(
          "registrationTitle",
          formData.registrationTitle.trim()
        );
        formDataToSend.append(
          "registrationSubtitle",
          formData.registrationSubtitle.trim()
        );
        if (files.registrationCoverImage) {
          formDataToSend.append(
            "registrationCoverImage",
            files.registrationCoverImage
          );
        }
      }

      // Waiting Room settings
      formDataToSend.append("countdownTimer", formData.countdownTimer);
      if (formData.countdownTimer) {
        const countdownMins = formData.countdownCustomMinutes
          ? parseInt(formData.countdownCustomMinutes)
          : parseInt(formData.countdownMinutes);
        formDataToSend.append("countdownMinutes", countdownMins);
      }
      formDataToSend.append(
        "waitingRoomTitle",
        formData.waitingRoomTitle.trim()
      );
      formDataToSend.append(
        "waitingRoomMessage",
        formData.waitingRoomMessage.trim()
      );
      if (files.waitingRoomCoverImage) {
        formDataToSend.append(
          "waitingRoomCoverImage",
          files.waitingRoomCoverImage
        );
      }

      // Recording settings
      formDataToSend.append("autoRecordWebinar", formData.autoRecordWebinar);
      formDataToSend.append("autoShareReplay", formData.autoShareReplay);
      if (formData.autoShareReplay) {
        formDataToSend.append("replayAudience", formData.replayAudience);
      }

      // Password settings
      formDataToSend.append(
        "requirePassword",
        formData.requirePassword.toString()
      );
      if (formData.requirePassword) {
        const passwordStr = formData.password.join("");
        formDataToSend.append("password", passwordStr);
        formDataToSend.append(
          "showPasswordInEmail",
          formData.showPasswordInEmail.toString()
        );
      }

      // Streaming settings
      if (formData.streamYouTubeUrl) {
        formDataToSend.append("streamYouTubeUrl", formData.streamYouTubeUrl);
      }
      if (formData.streamYouTubeKey) {
        formDataToSend.append("streamYouTubeKey", formData.streamYouTubeKey);
      }
      if (formData.streamFacebookUrl) {
        formDataToSend.append("streamFacebookUrl", formData.streamFacebookUrl);
      }
      if (formData.streamFacebookKey) {
        formDataToSend.append("streamFacebookKey", formData.streamFacebookKey);
      }
      if (formData.streamLinkedInUrl) {
        formDataToSend.append("streamLinkedInUrl", formData.streamLinkedInUrl);
      }
      if (formData.streamLinkedInKey) {
        formDataToSend.append("streamLinkedInKey", formData.streamLinkedInKey);
      }

      let response;
      if (isEditMode && roomId) {
        // Update existing webinar
        response = await updateScheduledMeeting(roomId, formDataToSend);
      } else {
        // Create new webinar
        response = await createScheduledMeeting(formDataToSend);
      }

      if (response && response.success !== false) {
        toast.success(
          isEditMode
            ? "Webinar updated successfully!"
            : "Webinar scheduled successfully!"
        );

        // Reset form to initial state
        const resetTime = getCurrentTime();
        const initialFormData = {
          webinarTitle: "",
          description: "",
          recurringEvent: true,
          frequency: "WEEKLY",
          startDate: getCurrentDate(),
          endDate: getCurrentDate(),
          startHour: resetTime.hour,
          startMinute: resetTime.minute,
          startPeriod: resetTime.period,
          duration: "60",
          customDuration: "",
          timezone: "America/New_York (GMT-05:00)",
          allowStageAccess: true,
          allowCoHostsManage: true,
          autoSpotlight: true,
          preAssignPresenters: false,
          presenterEmails: [],
          presenterEmailInput: "",
          showAttendeeList: true,
          showAttendeeCount: true,
          allowChat: true,
          allowReactions: true,
          customBackground: false,
          requireRegistration: false,
          registrationTitle: "",
          registrationSubtitle: "",
          countdownTimer: false,
          countdownMinutes: "15",
          countdownCustomMinutes: "",
          waitingRoomTitle: "",
          waitingRoomMessage: "",
          autoRecordWebinar: true,
          autoShareReplay: true,
          replayAudience: "attendees",
          requirePassword: true,
          password: ["", "", "", "", "", ""],
          showPasswordInEmail: true,
          streamYouTubeUrl: "",
          streamYouTubeKey: "",
          streamFacebookUrl: "",
          streamFacebookKey: "",
          streamLinkedInUrl: "",
          streamLinkedInKey: "",
        };

        // Only reset form if in create mode, not edit mode
        if (!isEditMode) {
          setFormData(initialFormData);
          setFiles({
            customBackgroundImage: null,
            registrationCoverImage: null,
            waitingRoomCoverImage: null,
          });
          setErrors({});
          setStreamingToggle({
            streamYouTube: false,
            streamFacebook: false,
            streamLinkedIn: false,
          });
          // Hide the form after successful creation
          setIsFormHidden(true);
        } else {
          // In edit mode, just clear errors
          setErrors({});
        }

        // Navigate to dashboard after successful submission
        setTimeout(() => {
          navigate("/");
          // Reload the page to refresh data
        }, 100);
      } else {
        toast.error(
          response?.message ||
            (isEditMode
              ? "Failed to update webinar. Please try again."
              : "Failed to schedule webinar. Please try again.")
        );
      }
    } catch (error) {
      console.error("Webinar scheduling error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // If form is hidden after successful submission, return null
  if (isFormHidden) {
    return null;
  }

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 w-full px-4 sm:px-8 md:px-16 lg:px-20 py-8 sm:py-10 md:py-16">
        <div className="text-white text-lg">Loading webinar data...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-10 w-full px-4 sm:px-8 md:px-16 lg:px-20 py-8 sm:py-10 md:py-16 rounded-2xl primary-card-background bg-opacity-12">
      <div className="text-white font-sans font-bold text-sm tracking-[3px]">
        WEBINAR DETAILS
      </div>

      <div className="flex flex-col items-start gap-5 w-full max-w-[632px]">
        <div className="flex flex-col items-start gap-2.5 w-full">
          <div className="text-white font-sans text-[13px] font-semibold">
            Webinar Title
          </div>
          <div className="flex flex-col items-end gap-1 w-full">
            <input
              type="text"
              placeholder="Enter webinar title"
              value={formData.webinarTitle}
              onChange={(e) =>
                handleInputChange("webinarTitle", e.target.value)
              }
              maxLength={100}
              className={`flex py-3 px-2.5 justify-center items-center gap-2.5 w-full rounded-md bg-white text-gray-500 text-[13px] placeholder-gray-500 focus:outline-none focus:ring-2 ${
                errors.webinarTitle
                  ? "border-2 border-red-500 focus:ring-red-500"
                  : "focus:ring-purple-550"
              }`}
            />
            <div className="w-full flex justify-between items-center">
              <div className="text-red-500 text-xs">{errors.webinarTitle}</div>
              <div className="text-gray-500 text-opacity-60 text-xs">
                {`${formData.webinarTitle.trim().length}/100 characters`}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start gap-2.5 w-full">
          <div className="text-white font-sans text-[13px] font-semibold">
            Description (Optional)
          </div>
          <div className="flex flex-col items-end gap-1 w-full">
            <textarea
              placeholder="Add webinar agenda or notes"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              maxLength={300}
              className="flex h-28 py-3 px-2.5 flex-col items-center gap-2.5 w-full rounded-md bg-white text-gray-500 text-[13px] placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-purple-550"
            />
            <div className="w-full flex justify-end items-center">
              <div className="text-gray-500 text-opacity-60 text-xs">
                {`${formData.description.trim().length}/300 characters`}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex w-full flex-wrap items-center gap-3 justify-start">
        <div className="w-full max-w-[160px] text-left text-white font-sans text-xl sm:text-2xl font-semibold">
          Date & Time
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={formatDisplayDate(formData.startDate) || "MM/DD/YYYY"}
            className="w-[140px] rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none"
          />
          <button
            type="button"
            onClick={() => setShowStartCalendar((prev) => !prev)}
            className="rounded-md border border-[#F4C2D7] bg-[#FFE5EE] p-2 transition-colors hover:bg-[#FAD0E3]"
          >
            <CalendarDays className="h-4 w-4 text-[#E06187]" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={`${formData.startHour}-${formData.startMinute}`}
            onChange={(e) => {
              const [hourStr, minuteStr] = e.target.value.split("-");
              handleInputChange("startHour", parseInt(hourStr, 10));
              handleInputChange("startMinute", minuteStr);
            }}
            className="rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
          >
            {TIME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={formData.startPeriod}
            onChange={(e) => handleInputChange("startPeriod", e.target.value)}
            className="rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>
      {showStartCalendar && (
        <div className="mt-4 w-full max-w-[320px] rounded-[20px] border border-[#2F2E4E] bg-[#1C1B29] p-4">
          <Calendar
            value={formData.startDate}
            minDate={new Date()}
            onChange={(val) => {
              handleInputChange("startDate", val);
              setShowStartCalendar(false);
              if (errors.startDate) {
                clearFieldError("startDate");
              }
            }}
            calendarType="gregory"
            className="text-white"
          />
        </div>
      )}

      <div className="flex w-full flex-wrap items-center gap-3 justify-start">
        <div className="w-full max-w-[160px] text-left text-white font-sans text-xl sm:text-2xl font-semibold">
          Duration
        </div>
        <div className="flex items-center gap-2">
          <select
            value={durationHours}
            onChange={(e) => {
              const hours = parseInt(e.target.value, 10);
              const totalMinutes = hours * 60 + durationMinutes;
              handleInputChange("duration", totalMinutes.toString());
              handleInputChange("customDuration", "");
            }}
            className="w-[80px] rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
          >
            {[...Array(13).keys()].map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">hr</span>
          <select
            value={durationMinutes}
            onChange={(e) => {
              const minutes = parseInt(e.target.value, 10);
              const totalMinutes = durationHours * 60 + minutes;
              handleInputChange("duration", totalMinutes.toString());
              handleInputChange("customDuration", "");
            }}
            className="w-[80px] rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
          >
            {[0, 15, 30, 45].map((minute) => (
              <option key={minute} value={minute}>
                {minute}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">min</span>
        </div>
      </div>

      <div className="flex w-full flex-wrap items-center gap-3 justify-start">
        <div className="w-full max-w-[160px] text-left text-white font-sans text-xl sm:text-2xl font-semibold">
          Timezone
        </div>
        <div className="flex items-center gap-2 flex-1">
          <div className="relative w-full max-w-md">
            <select
              value={formData.timezone}
              onChange={(e) => handleInputChange("timezone", e.target.value)}
              className="w-full appearance-none rounded-lg border border-[#D3D3E0] bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          </div>
        </div>
      </div>

      <div className="text-white font-sans font-bold text-sm tracking-widest">
        WEBINAR SETTINGS
      </div>

      <div className="flex flex-col justify-center items-start gap-10 w-full">
        <div className="text-white font-sans text-xl sm:text-2xl font-semibold">
          1. Stage Control
        </div>

        <div className="flex flex-col items-start gap-5 w-full">
          <div className="flex items-center gap-4">
            <ToggleSwitch
              enabled={formData.allowStageAccess}
              onToggle={() =>
                handleInputChange(
                  "allowStageAccess",
                  !formData.allowStageAccess
                )
              }
            />
            <div className="text-white font-sans text-base">
              Allow Stage Access
            </div>
            <div className="text-gray-400 font-sans text-base font-light">
              (Attendees can ask to join the stage.)
            </div>
          </div>

          <div className="flex items-center gap-4">
            <ToggleSwitch
              enabled={formData.allowCoHostsManage}
              onToggle={() =>
                handleInputChange(
                  "allowCoHostsManage",
                  !formData.allowCoHostsManage
                )
              }
            />
            <div className="text-white font-sans text-base">
              Allow Co-hosts to Manage Stage Requests
            </div>
            <div className="text-gray-400 font-sans text-base font-light">
              (Co-hosts can approve, deny, or remove requests.)
            </div>
          </div>

          <div className="flex items-center gap-4">
            <ToggleSwitch
              enabled={formData.autoSpotlight}
              onToggle={() =>
                handleInputChange("autoSpotlight", !formData.autoSpotlight)
              }
            />
            <div className="text-white font-sans text-base">
              Auto Spotlight (Active Speaker Mode)
            </div>
            <div className="text-gray-400 font-sans text-base font-light">
              (Automatically switch view to the person speaking.)
            </div>
          </div>

          <div className="flex flex-col justify-center items-start gap-2.5 w-full">
            <div className="flex items-center gap-4">
              <ToggleSwitch
                enabled={formData.preAssignPresenters}
                onToggle={() =>
                  handleInputChange(
                    "preAssignPresenters",
                    !formData.preAssignPresenters
                  )
                }
              />
              <div className="text-white font-sans text-base">
                Pre-assign Presenters / Co-hosts
              </div>
              <div className="text-gray-400 font-sans text-base font-light">
                (Add registered R-Link emails to auto-join stage.)
              </div>
            </div>
            {formData.preAssignPresenters && (
              <div className="flex flex-col gap-2.5 pl-12 w-full">
                <div className="flex flex-col gap-2 w-full max-w-[420px]">
                  <div className="flex w-full gap-2">
                    <input
                      type="email"
                      placeholder="Add emails of presenters or co-hosts"
                      value={formData.presenterEmailInput}
                      onChange={(e) =>
                        handleInputChange("presenterEmailInput", e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handlePresenterEmailAdd();
                        }
                      }}
                      className="flex-grow py-2.5 px-3 rounded-md bg-white text-gray-800 text-base placeholder-gray-400 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-550 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={handlePresenterEmailAdd}
                      className="px-4 py-2 rounded-md bg-[#832CFF] text-white font-semibold hover:bg-purple-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {formData.presenterEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.presenterEmails.map((email, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-[#832CFF] px-3 py-1 rounded-md"
                        >
                          <span className="text-white text-sm">{email}</span>
                          <button
                            type="button"
                            onClick={() => handlePresenterEmailRemove(email)}
                            className="text-white hover:text-red-300 text-lg font-bold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Removed redundant "+ Add More" button. The Add action is already handled by the primary Add button next to the input. */}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center items-start gap-10 w-full">
        <div className="text-white font-sans text-xl sm:text-2xl font-semibold">
          2. Attendee Options
        </div>

        <div className="flex flex-col items-start gap-5 w-full">
          <div className="flex items-center gap-4">
            <ToggleSwitch
              enabled={formData.showAttendeeList}
              onToggle={() =>
                handleInputChange(
                  "showAttendeeList",
                  !formData.showAttendeeList
                )
              }
            />
            <div className="text-white font-sans text-base">
              Show Attendee List
            </div>
            <div className="text-gray-400 font-sans text-base font-light">
              (Choose if attendees can see who's connected.)
            </div>
          </div>

          <div className="flex items-center gap-4">
            <ToggleSwitch
              enabled={formData.showAttendeeCount}
              onToggle={() =>
                handleInputChange(
                  "showAttendeeCount",
                  !formData.showAttendeeCount
                )
              }
            />
            <div className="text-white font-sans text-base">
              Show Attendee Count
            </div>
            <div className="text-gray-400 font-sans text-base font-light">
              (Display or hide the total number of viewers.)
            </div>
          </div>

          <div className="flex items-center gap-4">
            <ToggleSwitch
              enabled={formData.allowChat}
              onToggle={() =>
                handleInputChange("allowChat", !formData.allowChat)
              }
            />
            <div className="text-white font-sans text-base">Chat</div>
            <div className="text-gray-400 font-sans text-base font-light">
              (Enable or disable attendee chat.)
            </div>
          </div>

          <div className="flex items-center gap-4">
            <ToggleSwitch
              enabled={formData.allowReactions}
              onToggle={() =>
                handleInputChange("allowReactions", !formData.allowReactions)
              }
            />
            <div className="text-white font-sans text-base">Reactions</div>
            <div className="text-gray-400 font-sans text-base font-light">
              (Allow emoji reactions.)
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center items-start gap-10 w-full">
        <div className="text-white font-sans text-xl sm:text-2xl font-semibold">
          3. Customizations
        </div>

        <div className="text-white font-sans text-base tracking-[5px]">
          IN-CALL CUSTOMIZATION
        </div>

        <div className="flex flex-col items-center gap-5 w-full">
          <div className="flex flex-col items-start gap-5 w-full">
            <div className="flex items-center gap-4">
              <ToggleSwitch
                enabled={formData.customBackground}
                onToggle={() =>
                  handleInputChange(
                    "customBackground",
                    !formData.customBackground
                  )
                }
              />
              <div className="text-white font-sans text-base">
                Custom Background
              </div>
              <div className="text-gray-400 font-sans text-base font-light">
                (Personalize your backdrop for a fully branded meeting space)
              </div>
            </div>
          </div>

          {formData.customBackground && (
            <UploadFile
              label="Custom Background"
              recommendedText="Recommended: 1920 x 1080 for best quality. Max file size: 3 MB. JPG or PNG. Leave blank to display R-Link's default image."
              file={files.customBackgroundImage}
              onFileSelect={(file) =>
                handleFileSelect("customBackgroundImage", file)
              }
            />
          )}
        </div>
      </div>

      <div className="flex flex-col justify-center items-start gap-10 w-full">
        <div className="text-white font-sans text-base tracking-[5px]">
          REGISTRATION PAGE CUSTOMIZATION
        </div>

        <div className="flex flex-col justify-center items-start gap-5 w-full">
          <div className="flex items-center gap-4">
            <ToggleSwitch
              enabled={formData.requireRegistration}
              onToggle={() =>
                handleInputChange(
                  "requireRegistration",
                  !formData.requireRegistration
                )
              }
            />
            <div className="text-white font-sans text-base">
              Require Registration
            </div>
            <div className="text-gray-400 font-sans text-base font-light">
              (Personalize your backdrop for a fully branded meeting space)
            </div>
          </div>

          {formData.requireRegistration && (
            <div className="flex flex-col justify-center items-start gap-1 w-full pl-12">
              {/* <div className="text-white font-sans text-base">Title</div>
              <input
                type="text"
                placeholder="e.g. Welcome to [Webinar Name]"
                value={formData.registrationTitle}
                onChange={(e) =>
                  handleInputChange("registrationTitle", e.target.value)
                }
                maxLength={25}
                className={`flex py-2.5 px-3 items-center gap-2.5 w-full rounded-md bg-white text-gray-500 text-base placeholder-gray-500 focus:outline-none focus:ring-2 ${
                  errors.registrationTitle
                    ? "border-2 border-red-500 focus:ring-red-500"
                    : "focus:ring-purple-550"
                }`}
              />
              <div className="w-full flex justify-between items-center">
                <div className="text-red-500 text-xs">
                  {errors.registrationTitle}
                </div>
                <div className="text-white text-right text-[7px]">
                  25 characters max
                </div>
              </div>

              <div className="text-white font-sans text-base mt-4">
                Subtitle
              </div>
              <input
                type="text"
                placeholder="e.g. Reserve your seat below and get the access link instantly."
                value={formData.registrationSubtitle}
                onChange={(e) =>
                  handleInputChange("registrationSubtitle", e.target.value)
                }
                maxLength={25}
                className={`flex py-2.5 px-3 items-center gap-2.5 w-full rounded-md bg-white text-gray-500 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 ${
                  errors.registrationSubtitle
                    ? "border-2 border-red-500 focus:ring-red-500"
                    : "focus:ring-purple-550"
                }`}
              />
              <div className="w-full flex justify-between items-center">
                <div className="text-red-500 text-xs">
                  {errors.registrationSubtitle}
                </div>
                <div className="text-white text-right text-[7px]">
                  25 characters max
                </div>
              </div> */}

              <div className="text-white font-sans text-base mt-4">
                Upload a Cover Image
              </div>
              <UploadFile
                file={files.registrationCoverImage}
                onFileSelect={(file) =>
                  handleFileSelect("registrationCoverImage", file)
                }
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col justify-center items-start gap-4 w-full">
        <div className="flex flex-col justify-center items-start gap-10 w-full">
          <div className="text-white font-sans text-base tracking-[5px]">
            Waiting Room
          </div>

          <div className="flex flex-col items-start gap-5 w-full">
            <div className="flex items-center gap-4 w-full">
              <ToggleSwitch
                enabled={formData.countdownTimer}
                onToggle={() =>
                  handleInputChange("countdownTimer", !formData.countdownTimer)
                }
              />
              <div className="text-white font-sans text-base">Waiting Room</div>
              <div className="text-gray-400 font-sans text-base font-light">
                (Enable a countdown showing how long until the webinar begins).
              </div>
            </div>

            {formData.countdownTimer && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 pl-12 flex-wrap">
                <div className="text-white font-sans text-base whitespace-nowrap">
                  Start showing timer
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {["15", "30", "45", "60", "90"].map((value) => (
                    <DurationButton
                      key={value}
                      value={value}
                      selected={formData.countdownMinutes === value}
                      onClick={() =>
                        handleInputChange("countdownMinutes", value)
                      }
                    />
                  ))}
                  <div className="flex items-center gap-2.5">
                    <span className="text-white font-montserrat text-sm">
                      Custom:
                    </span>
                    <div className="flex flex-col gap-1">
                      <input
                        type="text"
                        value={formData.countdownCustomMinutes}
                        onChange={(e) =>
                          handleInputChange(
                            "countdownCustomMinutes",
                            e.target.value
                          )
                        }
                        className={`w-11 h-10 px-1.5 py-4 rounded-lg bg-[#0000001F] text-white text-center focus:outline-none focus:ring-2 ${
                          errors.countdownCustomMinutes
                            ? "border-2	border-red-500 focus:ring-red-500"
                            : "focus:ring-purple-550"
                        }`}
                      />
                      {errors.countdownCustomMinutes && (
                        <div className="text-red-500 text-xs whitespace-nowrap">
                          {errors.countdownCustomMinutes}
                        </div>
                      )}
                    </div>
                    <span className="text-gray-500 font-montserrat text-sm">
                      Minutes
                    </span>
                  </div>
                </div>
              </div>
            )}

            {formData.countdownTimer && (
              <div className="flex flex-col justify-center items-start gap-1 w-full pl-12">
                <div className="text-white font-sans text-base mt-4">
                  Upload a Cover Image
                </div>
                <UploadFile
                  file={files.waitingRoomCoverImage}
                  onFileSelect={(file) =>
                    handleFileSelect("waitingRoomCoverImage", file)
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowAdvancedSettings((prev) => !prev)}
        className="self-start rounded-full border border-white/20 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10"
      >
        {showAdvancedSettings
          ? "Hide Advanced Settings"
          : "Show Advanced Settings"}
      </button>
      {showAdvancedSettings && (
        <>
          <div className="flex flex-col justify-center items-start gap-10 w-full">
            <div className="text-white font-sans text-xl sm:text-2xl font-semibold">
              4. Recording & Replay
            </div>

            <div className="flex flex-col items-start gap-5 w-full">
              <div className="flex items-center gap-4">
                <ToggleSwitch
                  enabled={formData.autoRecordWebinar}
                  onToggle={() =>
                    handleInputChange(
                      "autoRecordWebinar",
                      !formData.autoRecordWebinar
                    )
                  }
                />
                <div className="text-white font-sans text-base">
                  Auto-record Webinar
                </div>
                <div className="text-gray-400 font-sans text-base font-light">
                  (Recording starts when the session goes live. You can
                  pause/stop anytime.)
                </div>
              </div>

              <div className="flex flex-col items-start gap-2.5 w-full">
                <div className="flex items-center gap-4 w-full">
                  <ToggleSwitch
                    enabled={formData.autoShareReplay}
                    onToggle={() =>
                      handleInputChange(
                        "autoShareReplay",
                        !formData.autoShareReplay
                      )
                    }
                  />
                  <div className="text-white font-sans text-base">
                    Automatically share replay link
                  </div>
                  <div className="text-gray-400 font-sans text-base font-light">
                    (Send the replay to your audience once it's ready.)
                  </div>
                </div>

                {formData.autoShareReplay && (
                  <div className="flex flex-col items-start gap-2.5 pl-14">
                    <div
                      className="flex items-center gap-2.5 cursor-pointer"
                      onClick={() =>
                        handleInputChange("replayAudience", "attendees")
                      }
                    >
                      <div className="w-5 h-5 rounded-full bg-customGray-400 flex items-center justify-center">
                        <div
                          className={`w-2.5 h-2.5 rounded-full ${
                            formData.replayAudience === "attendees"
                              ? "bg-[#832CFF]"
                              : ""
                          }`}
                        />
                      </div>
                      <div className="text-white font-sans text-base">
                        Attendees only
                      </div>
                    </div>
                    <div
                      className="flex	items-center gap-2.5 cursor-pointer"
                      onClick={() =>
                        handleInputChange("replayAudience", "registrants")
                      }
                    >
                      <div className="w-5 h-5 rounded-full bg-customGray-400 flex items-center justify-center">
                        <div
                          className={`w-2.5 h-2.5 rounded-full ${
                            formData.replayAudience === "registrants"
                              ? "bg-[#832CFF]"
                              : ""
                          }`}
                        />
                      </div>
                      <div className="text-white font-sans text-base">
                        All registrants
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {showAdvancedSettings && (
        <>
          <div className="flex flex-col justify-center items-start gap-10 w-full">
            <div className="text-white font-sans text-xl sm:text-2xl font-semibold">
              5. Access Security
            </div>

            <div className="flex flex-col items-start gap-5 w-full">
              <div className="flex flex-col items-start gap-2.5 w-full">
                <div className="flex items-center gap-4 w-full">
                  <ToggleSwitch
                    enabled={formData.requirePassword}
                    onToggle={() =>
                      handleInputChange(
                        "requirePassword",
                        !formData.requirePassword
                      )
                    }
                  />
                  <div className="text-white font-sans text-base">
                    Require password to join
                  </div>
                  <div className="text-gray-400 font-sans text-base font-light">
                    (Enable a password for this meeting or webinar.)
                  </div>
                </div>

                {formData.requirePassword && (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pl-12 w-full">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1">
                        {[0, 1, 2, 3].map((i) => (
                          <input
                            key={i}
                            type="text"
                            maxLength={1}
                            value={formData.password[i] || ""}
                            onChange={(e) => {
                              handlePasswordChange(i, e.target.value);
                              // Clear password error when user starts typing
                              if (errors.password) clearFieldError("password");
                              // Auto-focus next input if value entered and not last input
                              if (e.target.value && i < 5) {
                                const nextInput =
                                  e.target.parentElement.children[i + 1];
                                if (nextInput) nextInput.focus();
                              }
                            }}
                            onKeyDown={(e) => {
                              // Handle backspace to move to previous input
                              if (
                                e.key === "Backspace" &&
                                !e.target.value &&
                                i > 0
                              ) {
                                const prevInput =
                                  e.target.parentElement.children[i - 1];
                                if (prevInput) prevInput.focus();
                              }
                              // Handle arrow keys for navigation
                              if (e.key === "ArrowLeft" && i > 0) {
                                const prevInput =
                                  e.target.parentElement.children[i - 1];
                                if (prevInput) prevInput.focus();
                              }
                              if (e.key === "ArrowRight" && i < 5) {
                                const nextInput =
                                  e.target.parentElement.children[i + 1];
                                if (nextInput) nextInput.focus();
                              }
                            }}
                            className={`w-10 h-10 rounded-xl bg-white text-center text-black text-lg font-semibold focus:outline-none focus:ring-2 ${
                              errors.password
                                ? "border-2 border-red-500 focus:ring-red-500"
                                : "focus:ring-purple-550"
                            }`}
                          />
                        ))}
                      </div>
                      {errors.password && (
                        <div className="text-red-500 text-xs">
                          {errors.password}
                        </div>
                      )}
                    </div>
                    <div className="text-gray-400 text-center text-base font-light leading-4">
                      4 characters, letters or numbers.
                    </div>
                  </div>
                )}
              </div>

              {/* <div className="flex items-center gap-4">
                <ToggleSwitch
                  enabled={formData.showPasswordInEmail}
                  onToggle={() =>
                    handleInputChange(
                      "showPasswordInEmail",
                      !formData.showPasswordInEmail
                    )
                  }
                />
                <div className="text-white font-sans text-base">
                  Show password in invitation email
                </div>
                <div className="text-gray-400 font-sans text-base font-light">
                  (Include password automatically in invites..)
                </div>
              </div> */}
            </div>
          </div>

          <div className="flex flex-col justify-center items-start gap-8 w-full">
            <div className="text-white font-sans text-xl sm:text-2xl font-semibold">
              6. Streaming
            </div>

            <div className="w-full space-y-4">
              {[
                {
                  key: "streamYouTube",
                  label: "YouTube",
                  icon: "/youtube.svg",
                  urlField: "streamYouTubeUrl",
                  keyField: "streamYouTubeKey",
                },
                {
                  key: "streamFacebook",
                  label: "Facebook",
                  icon: "/facebook.svg",
                  urlField: "streamFacebookUrl",
                  keyField: "streamFacebookKey",
                },
                {
                  key: "streamLinkedIn",
                  label: "LinkedIn",
                  icon: "/linkedin.svg",
                  urlField: "streamLinkedInUrl",
                  keyField: "streamLinkedInKey",
                },
              ].map(({ key, label, icon, urlField, keyField }) => {
                const isEnabled = streamingToggle[key];
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-3 rounded-2xl p-4 md:p-5"
                  >
                    <div className="flex items-center gap-3">
                      <ToggleSwitch
                        enabled={isEnabled}
                        onToggle={() => {
                          if (isEnabled) {
                            handleInputChange(urlField, "");
                            handleInputChange(keyField, "");
                            setStreamingToggle((prev) => ({
                              ...prev,
                              [key]: false,
                            }));
                          } else {
                            setStreamingToggle((prev) => ({
                              ...prev,
                              [key]: true,
                            }));
                          }
                        }}
                      />
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-md bg-white flex items-center justify-center overflow-hidden">
                          <img
                            src={icon}
                            alt={`${label} logo`}
                            className="h-6 w-6 object-contain"
                          />
                        </div>
                        <span className="text-white font-sans text-lg font-semibold">
                          {label}
                        </span>
                      </div>
                    </div>

                    {isEnabled && (
                      <div className="flex flex-col gap-3">
                        <input
                          type="text"
                          placeholder={`Enter ${label} stream URL`}
                          value={formData[urlField]}
                          onChange={(e) =>
                            handleInputChange(urlField, e.target.value)
                          }
                          className="flex w-full items-center gap-2.5 rounded-md bg-white py-2.5 px-3 text-base text-gray-600 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-550"
                        />
                        <input
                          type="text"
                          placeholder={`Enter ${label} stream key`}
                          value={formData[keyField]}
                          onChange={(e) =>
                            handleInputChange(keyField, e.target.value)
                          }
                          className="flex w-full items-center gap-2.5 rounded-md bg-white py-2.5 px-3 text-base text-gray-600 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-550"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
      <button
        onClick={handleSubmit}
        disabled={isSubmitting}
        className={`flex py-3 px-24 justify-center items-center gap-2 rounded-full bg-green-500 transition-colors ${
          isSubmitting
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-green-600 cursor-pointer"
        }`}
      >
        <div className="text-white font-sans text-xs font-semibold leading-4 tracking-tight">
          {isSubmitting
            ? isEditMode
              ? "Updating..."
              : "Scheduling..."
            : isEditMode
            ? "Update Webinar"
            : "Schedule Webinar"}
        </div>
      </button>
    </div>
  );
};

export default WebinarScheduleForm;
